import { loadServiceDetailsByService } from '../../../store/directusService';
import {
    POLARIS_GREY,
    POLARIS_METROPOLITAN_CHERRY,
    POLARIS_ROYAL_BLUE,
    POLARIS_WHITE
} from '../../../themes/theme';
import {
    ADVISER,
    AUDIT,
    hasRole,
    LOCAL_ADMIN,
    MANAGER,
    PRAP,
    QUALITY,
    READ_ONLY,
    RECRUITMENT_MANAGER,
    SELF_EMPLOYED_COACH,
    SUPERUSER
} from '../../../utils/userRoles';

export const getBusinessHours = () => {
    return {
        startTime: '08:00',
        endTime: '18:00',
        daysOfWeek: [1, 2, 3, 4, 5]
    };
};

export const getServiceIds = (user) => {
    let serviceIds = [];
    if ('primaryService' in user && 'otherServices' in user) {
        serviceIds = [user.primaryService, ...user.otherServices].filter((el) => el);
    } else if ('serviceIds' in user) serviceIds = user.serviceIds;
    return Array.isArray(serviceIds) ? serviceIds : [];
};

export const fetchUserServiceIds = (user, dispatch) => {
    const serviceIds = getServiceIds(user);
    serviceIds?.length > 0 && dispatch(loadServiceDetailsByService(serviceIds));
    return serviceIds;
};

export const mapCommonParams = (el) => {
    const date = el.date?.slice(0, 10) || '';
    const start = date ? `${date}T${el.startTime}` : '';
    const end = date ? `${date}T${el.endTime}` : '';
    return { date, start, end };
};

export const mapColors = (id, currentId) => {
    let color, textColor;
    if (id === currentId) {
        color = POLARIS_GREY;
        textColor = POLARIS_ROYAL_BLUE;
    } else {
        color = POLARIS_ROYAL_BLUE;
        textColor = POLARIS_WHITE;
    }
    return { color, textColor };
};

export const mapUserEventColors = (id, currentId = '') => {
    let color, textColor;
    if (id === currentId) {
        color = POLARIS_GREY;
        textColor = POLARIS_METROPOLITAN_CHERRY;
    } else {
        color = POLARIS_METROPOLITAN_CHERRY;
        textColor = POLARIS_WHITE;
    }
    return { color, textColor };
};

const formatParticipants = (participantId, participants = {}) => {
    let participantEntry = {};
    let participantName = '';
    if (Object.keys(participants).length) {
        if (participantId) {
            participantEntry =
                participantId in participants
                    ? participants[participantId]
                    : { noMatchingService: true };
            participantName = participantEntry.firstName
                ? `${participantEntry.firstName} ${participantEntry?.lastName || ''}`
                : '';
        }
    }
    return { participantEntry, participantName };
};

const formatParticipantByArray = (participantId, participants = []) => {
    let participantEntry = null;
    let participant = '';
    if (participants.length && participantId) {
        participantEntry = participants?.find((entry) => entry.id === participantId);
        participant = participantEntry
            ? `${participantEntry?.firstName} ${participantEntry?.lastName}`
            : '';
    }
    return { participantEntry, participant };
};

const formatCommsLinks = (el, slots) => {
    const statusesForCommLinks = ['not known', 'failed to attend'];
    const displayCommLinks =
        !slots && el?.attendances?.length
            ? statusesForCommLinks.includes(el.attendances[0]?.status?.toLowerCase())
            : false;
    return { displayCommLinks };
};

export const formatSlots = (el) => {
    let nonCancelledAttendancesLength = 0;
    if ('numberOfSlots' in el) {
        nonCancelledAttendancesLength =
            el.attendances.filter((entry) => !cancelledStatuses.includes(entry.status))?.length ||
            0;
    }
    const slots = el?.numberOfSlots;
    const filled = nonCancelledAttendancesLength;
    const filledSlots = `${filled}/${slots}`;
    return { slots, filledSlots };
};

// userCalendar (uses PT object now)
export const configParticipantAndCommsEventContent = (el, participants) => {
    const { participantEntry, participantName } = formatParticipants(
        el.participantId,
        participants
    );
    const { slots, filledSlots } = formatSlots(el);
    const { displayCommLinks } = formatCommsLinks(el, slots);
    return { participantEntry, participantName, slots, filledSlots, displayCommLinks };
};

// PT calendar - doesn't need PT config.
export const configEventContent = (el) => {
    const { slots, filledSlots } = formatSlots(el);
    const { displayCommLinks } = formatCommsLinks(el, slots);
    return { slots, filledSlots, displayCommLinks };
};

// schedulingAssistant
export const configParticipantEventContent = (el, participants) => {
    const { participantEntry, participant } = formatParticipantByArray(
        el.participantId,
        participants
    );
    const { slots, filledSlots } = formatSlots(el);
    return { participantEntry, participant, slots, filledSlots };
};

export const configUserEventContent = (el) => {
    const displayOccurrence = el.repeat !== 'NO_REPEAT';
    const content = `${el.userEventType}`;
    return { displayOccurrence, content };
};

export const configCourseEventContent = (el) => {
    return `${el.courseName} - ${el.title}`.concat(`${el?.location ? ` - ${el.location}` : ''}`);
};

export const formatFullCalendarTimes = (time) => {
    const { start, end } = time;
    const date = start?.slice(0, 10);
    const startTime = start?.split('T')[1]?.slice(0, 5);
    const endTime = end?.split('T')[1]?.slice(0, 5);
    return { date, startTime, endTime };
};

export const unresolvedStatuses = ['Not known', 'Cancelled - Adviser', 'Cancelled - Participant'];
export const cancelledStatuses = ['Cancelled - Adviser', 'Cancelled - Participant'];

export const group1 = [QUALITY, SUPERUSER];
export const group2 = [ADVISER, MANAGER, RECRUITMENT_MANAGER];
export const group3 = [AUDIT, LOCAL_ADMIN, PRAP, READ_ONLY, SELF_EMPLOYED_COACH];
export const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
export const deleteRoles = [SUPERUSER];
export const futureDateEditRoles = [ADVISER, MANAGER];
export const adviserRoles = [ADVISER, MANAGER, RECRUITMENT_MANAGER, SELF_EMPLOYED_COACH];
export const eventEditRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
export const auditRoles = [
    ADVISER,
    AUDIT,
    LOCAL_ADMIN,
    MANAGER,
    PRAP,
    QUALITY,
    READ_ONLY,
    RECRUITMENT_MANAGER,
    SUPERUSER
];
export const viewAdviserRoles = [
    ADVISER,
    AUDIT,
    LOCAL_ADMIN,
    MANAGER,
    PRAP,
    QUALITY,
    READ_ONLY,
    RECRUITMENT_MANAGER,
    SUPERUSER
];
export const participantAccessRoles = [
    ADVISER,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SELF_EMPLOYED_COACH,
    SUPERUSER
];
export const conductAppointmentRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const calendarEditPermissions = {
    appointment: {
        notKnown: {
            service: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            adviser: {
                group1: { past: true, notPast: true },
                group2: { past: true, notPast: true },
                group3: { past: false, notPast: false }
            },
            appointmentType: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: true },
                group3: { past: false, notPast: false }
            },
            location: {
                group1: { past: true, notPast: true },
                group2: { past: true, notPast: true },
                group3: { past: false, notPast: false }
            },
            startAndEndTime: {
                group1: { past: true, notPast: true },
                group2: { past: true, notPast: true },
                group3: { past: false, notPast: false }
            },
            date: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false },
                futureDateEditGroup: { past: false, notPast: true }
            },
            participant: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            mandatory: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            updateButton: {
                group1: { past: true, notPast: true },
                group2: { past: true, notPast: true },
                group3: { past: false, notPast: false }
            }
        },
        notNotKnown: {
            service: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            adviser: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            appointmentType: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            location: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            startAndEndTime: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            date: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false },
                futureDateEditGroup: { past: false, notPast: false }
            },
            participant: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            mandatory: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            updateButton: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            }
        }
    },
    slot: {
        unresolvedStatus: {
            //  ['Not known', 'Cancelled - Adviser', 'Cancelled - Participant'];
            service: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            team: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            adviser: {
                group1: { past: true, notPast: true },
                group2: { past: true, notPast: true },
                group3: { past: false, notPast: false }
            },
            appointmentType: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            location: {
                group1: { past: true, notPast: true },
                group2: { past: true, notPast: true },
                group3: { past: false, notPast: false }
            },
            startAndEndTime: {
                group1: { past: true, notPast: true },
                group2: { past: true, notPast: true },
                group3: { past: false, notPast: false }
            },
            date: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false },
                futureDateEditGroup: { past: false, notPast: true }
            },
            participant: {
                group1: { past: true, notPast: true },
                group2: { past: true, notPast: true },
                group3: { past: false, notPast: false }
            },
            removeParticipant: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            numOfSlots: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            updateButton: {
                group1: { past: true, notPast: true },
                group2: { past: true, notPast: true },
                group3: { past: false, notPast: false }
            }
        },
        resolvedStatus: {
            // ['Attended', 'Failed to Attend']
            service: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            team: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            adviser: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            appointmentType: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            location: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            startAndEndTime: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            date: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false },
                futureDateEditGroup: { past: false, notPast: false }
            },
            participant: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            removeParticipant: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            numOfSlots: {
                group1: { past: false, notPast: false },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            },
            updateButton: {
                group1: { past: true, notPast: true },
                group2: { past: false, notPast: false },
                group3: { past: false, notPast: false }
            }
        }
    }
};

export const canEditAppointment = (isAppointmentKnown, field, userRoles, isPast) => {
    let group = 'noRoles';
    if (hasRole(group1, userRoles)) group = 'group1';
    else if (field === 'date' && hasRole(futureDateEditRoles, userRoles))
        group = 'futureDateEditGroup';
    else if (hasRole(group2, userRoles)) group = 'group2';
    else if (hasRole(group3, userRoles)) group = 'group3';

    const time = isPast ? 'past' : 'notPast';
    const status = isAppointmentKnown ? 'notNotKnown' : 'notKnown';
    return group === 'noRoles'
        ? false
        : calendarEditPermissions.appointment[status][field][group][time];
};

export const canEditSlot = (areAnySlotsResolved, field, userRoles, isPast) => {
    let group = 'noRoles';
    if (hasRole(group1, userRoles)) group = 'group1';
    else if (field === 'date' && hasRole(futureDateEditRoles, userRoles))
        group = 'futureDateEditGroup';
    else if (hasRole(group2, userRoles)) group = 'group2';
    else if (hasRole(group3, userRoles)) group = 'group3';
    const time = isPast ? 'past' : 'notPast';
    const status = areAnySlotsResolved ? 'resolvedStatus' : 'unresolvedStatus';
    return group === 'noRoles' ? false : calendarEditPermissions.slot[status][field][group][time];
};

// Course Calendar permissions
export const moduleAccessRoles = [ADVISER, MANAGER, QUALITY, SELF_EMPLOYED_COACH, SUPERUSER];
export const editModuleRoles = [ADVISER, MANAGER, QUALITY, SUPERUSER];
export const editPastModuleRoles = [MANAGER, QUALITY, SUPERUSER];
export const editPastModuleTimeRoles = [QUALITY, SUPERUSER];

const courseCalendarPermissions = {
    location: {
        editModuleRoles: { past: false, notPast: true },
        editPastModuleRoles: { past: true, notPast: true },
        editPastModuleTimeRoles: { past: true, notPast: true }
    },
    capacity: {
        editModuleRoles: { past: false, notPast: true },
        editPastModuleRoles: { past: true, notPast: true },
        editPastModuleTimeRoles: { past: true, notPast: true }
    },
    startAndEndTime: {
        editModuleRoles: { past: false, notPast: true },
        editPastModuleRoles: { past: false, notPast: true },
        editPastModuleTimeRoles: { past: true, notPast: true }
    },
    date: {
        editModuleRoles: { past: false, notPast: true },
        editPastModuleRoles: { past: false, notPast: true },
        editPastModuleTimeRoles: { past: true, notPast: true }
    },
    button: {
        editModuleRoles: { past: false, notPast: true },
        editPastModuleRoles: { past: true, notPast: true },
        editPastModuleTimeRoles: { past: true, notPast: true }
    }
};

export const canEditModule = (field, userRoles, isPast) => {
    let group = 'noRoles';
    if (hasRole(editPastModuleTimeRoles, userRoles)) group = 'editPastModuleTimeRoles';
    else if (hasRole(editPastModuleRoles, userRoles)) group = 'editPastModuleRoles';
    else if (hasRole(editModuleRoles, userRoles)) group = 'editModuleRoles';
    const time = isPast ? 'past' : 'notPast';
    return group === 'noRoles' ? false : courseCalendarPermissions[field][group][time];
};
